import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useReactToPrint } from 'react-to-print';
import axios from 'config/axios';
import Header from 'components/Header';
import Table from 'components/Table';
import Loader from 'components/Loader';
import QuestionPopup from 'components/QuestionPopup';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

function ClassAssignmentReport () {
    const [assignmentData, setAssignmentData] = useState(null);
    const [questionId, setQuestionId] = useState(null);
    const [questionData, setQuestionData] = useState(null);

    const { classId, assignmentId } = useParams();
    const printRef = useRef(null);
    const questionPopupRef = useOutsideClickHandler(() => handleQuestionPopupExit());
    const navigate = useNavigate();

    const handleQuestionPopup = (questionId: string) => {
        setQuestionId(questionId)
        axios.get(`tutors/classes/report/question/${questionId}/`)
            .then(res => {
                setQuestionData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleQuestionPopupExit = () => {
        setQuestionId(null);
        setQuestionData(null);
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    useEffect(() => {
        axios.get(`tutors/classes/${classId}/assignment/report/${assignmentId}/`)
            .then(res => {
                setAssignmentData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        
    }, [questionData])

    if (assignmentData === null) return <Loader />;

    return (
        <div className='classAssignmentReport'>
            <Helmet>
                <title>
                    Class Assignment Report - Test Center Admin
                </title>
            </Helmet>
            {questionData &&
                <QuestionPopup
                    info={{
                        ...questionData.info,
                        moduleName: '',
                        questionNumber: assignmentData.questions.findIndex(question => question === questionId) + 1
                    }}
                    correctStudent={assignmentData.students.find(student => (
                        student.answers && student.answers.find(a => a.questionId === questionId)
                    )) ? `${(100*assignmentData.students.filter(student => (
                        student.answers && student.answers.find(a => a.questionId === questionId && a.isCorrect)
                    )).length/assignmentData.students.filter(student => (
                        student.answers && student.answers.find(a => a.questionId === questionId)
                    )).length).toFixed(1)}%` : null}
                    testName={assignmentData.name}
                    question={questionData.question}
                    passages={questionData.passages}
                    figures={questionData.figures}
                    choices={questionData.choices}
                    correctChoice={questionData.correctChoice}
                    correctAnswers={questionData.correctAnswers}
                    manageExit={handleQuestionPopupExit}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={assignmentData.name}
                prevRoutes={[{ name: assignmentData.className, path: `/classes/${classId}` }]}
            />
            <div className='classAssignmentReport__subHeaderRow'>
                <h2 className='classAssignmentReport__subHeader'>
                    Scores by students
                </h2>
            </div>
            <div className='classAssignmentReport__scoresContainer'>
                <Table
                    className='classAssignmentReport__table'
                    header={[
                        'Students',
                        <p className='classAssignmentReport__cellCenter'>Score</p>,
                        <p className='classAssignmentReport__cellCenter'>Progress</p>
                    ]}
                    columnTypes={[
                        'string',
                        'number',
                        null
                      ]}
                    rows={assignmentData.students.map(student => ([
                        `${student.firstName} ${student.lastName}`,
                        student.score,
                        <div className='classAssignmentReport__progress classAssignmentReport__cellCenter'>
                            <div className={student.score
                                ? 'classAssignmentReport__progressBar--filled'
                                : student.startDate
                                ? 'classAssignmentReport__progressBar--started'
                                : 'classAssignmentReport__progressBar'
                            }></div>
                        </div>
                    ]))}
                    onSelectCell={assignmentData.students.map(student => ([
                        () => navigate(`/students/${student.studentId}`),
                        student.score ? () => navigate(`/review/assignment/${student.studentQuestionSetId}`) : null,
                        null
                    ]))}
                    navigator={false}
                />
            </div>
            <div className='classAssignmentReport__subHeaderRow'>
                <h2 className='classAssignmentReport__subHeader'>
                    Student responses
                </h2>
            </div>
            <Table
                header={['Students', ...assignmentData.questions.map((questionId, q) => (
                    <p 
                        className={`
                            classAssignmentReport__cellCenter
                            classAssignmentReport__response 
                            classAssignmentReport__cursor
                            classAssignmentReport__tableHeader
                        `}
                        onClick={() => handleQuestionPopup(questionId)}
                    >
                        Q{q+1}
                    </p>
                ))]}
                rows={[
                    ...assignmentData.students.filter(student => student.answers).map(student => ([
                        `${student.firstName} ${student.lastName}`,
                        ...student.answers.map(answer => (
                            <p className={`classAssignmentReport__cellCenter classAssignmentReport__response ${!answer.isCorrect && (answer.answer
                                ? 'classAssignmentReport__cellWrong'
                                : 'classAssignmentReport__cellSkipped'
                            )}`}>
                                {answer.answer ? answer.answer : '-'}
                            </p>
                        ))
                    ])),
                    [
                        <p className='classAssignmentReport__header'># Incorrect</p>,
                        ...assignmentData.questions.map((questionId) => (
                            <p className='classAssignmentReport__header classAssignmentReport__cellCenter'>
                                {assignmentData.students.filter(student => (
                                    student.answers &&
                                    !student.answers.find(answer => answer.questionId === questionId).isCorrect
                                )).length
                            }</p>
                        ))
                    ],
                ]}
                navigator={false}
            />
        </div>
    );
}

export default ClassAssignmentReport;